import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  breakpointAnalysisUrl,
  captableUrl,
  cashFlowLedgerUrl,
  financialUrl,
  firmSummaryUrl,
  fundOwnershipUrl,
  fundScheduleOfInvestmentsUrl,
  fundSummaryUrl,
  valuationUrl,
} from 'common/config/urls';
import {
  PDF_EXPORT_QUERY_PARAM_EXPORT_MODE,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TAB,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET,
} from 'common/constants/pdfExport';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { ValuationByAllocation, ValuationsApproach } from 'common/types/valuation';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import {
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_DISCOUNT_CASH_FLOW_APPROACH,
  VALUATIONS_EXTERNAL_VALUATION_APPROACH,
  VALUATIONS_FUTURE_EXIT_APPROACH,
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
  VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH,
  VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { FIRM_ROUTES } from '../../../pages/Firms/firm.routes';
import {
  CompanyPdfOptions,
  PdfExportConfiguration,
  PdfExportOption,
} from '../PdfExportConfigDialog.types';
import { PdfExportSupportedQueryParams, PdfExportTargetMapping, PdfPage } from './types';

const getRouteSlugById = (routes: { id: string; slug?: string }[], id: string): string | undefined =>
  routes.find(r => r.id === id)?.slug;

const withQuery: (path: string, params: PdfExportSupportedQueryParams) => string = (path, params) => {
  const url = new URL(path, window.location.origin);
  if (params.date) {
    url.searchParams.set('date', params.date);
  }
  if (params.version) {
    url.searchParams.set('version', params.version);
  }
  if (params.exportMode) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_MODE, params.exportMode);
  }
  if (params.exportTarget) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET, params.exportTarget);
  }
  if (params.exportTab) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_TAB, params.exportTab);
  }
  return url.toString();
};

/**
 * Hook to build dynamic URLs for PDF export based on current context and selected configuration
 */
export const usePdfPages = (
  configuration: PdfExportConfiguration,
  valuationInfo?: ValuationByAllocation | null
): PdfPage[] => {
  const location = useLocation();
  const params = useParams();
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo } = storeValues;

  return useMemo(() => {
    // Build URLs dynamically based on current context and configuration
    const firmSlug = firmInfo?.slug || params.firmSlugParam;
    const companySlug = companyInfo?.slug || params.companySlugParam;
    const fundSlug = params.fundSlugParam;

    // Extract current query parameters
    const currentSearchParams = new URLSearchParams(location.search);
    const measurementDate = currentSearchParams.get('date');
    const version = currentSearchParams.get('version');

    // Common query params
    const commonQuery = {
      date: measurementDate,
      version,
      exportMode: 'true',
    } as const;

    // Build URLs based on selected configuration
    const pages: PdfPage[] = [];

    const pushPage = (
      builder: (fs: string, cs: string) => string,
      title: string,
      exportTarget: string,
      exportTab?: string
    ) => {

      pages.push({
        url: withQuery(builder(firmSlug!, companySlug!), { ...commonQuery, exportTarget, exportTab }),
        title,
        exportTarget,
        exportTab,
      });
    };

    const companyOptionMapping: Record<
      keyof CompanyPdfOptions,
      Array<{
        urlBuilder: (fs: string, cs: string) => string;
        target: PdfExportTargetMapping;
      }>
    > = {
      valuationSummary: [{ urlBuilder: valuationUrl, target: PdfExportTargets.VALUATION_SUMMARY }],
      equityAllocation: [{ urlBuilder: valuationUrl, target: PdfExportTargets.EQUITY_ALLOCATION }],
      concludedShareValues: [{ urlBuilder: valuationUrl, target: PdfExportTargets.CONCLUDED_SHARE_VALUES }],
      financials: [{ urlBuilder: financialUrl, target: PdfExportTargets.FINANCIALS }],
      capTable: [{ urlBuilder: captableUrl, target: PdfExportTargets.CAP_TABLE }],
      fundOwnership: [{ urlBuilder: fundOwnershipUrl, target: PdfExportTargets.FUND_OWNERSHIP }],
      breakpointAnalysis: [{ urlBuilder: breakpointAnalysisUrl, target: PdfExportTargets.BREAKPOINT_ANALYSIS }],
      cashFlowLedger: [{ urlBuilder: cashFlowLedgerUrl, target: PdfExportTargets.CASH_FLOW_LEDGER }],
      approachBacksolve: [{ urlBuilder: valuationUrl, target: PdfExportTargets.BACKSOLVE }],
      approachDcf: [{ urlBuilder: valuationUrl, target: PdfExportTargets.DISCOUNTED_CASH_FLOW }],
      approachExternalValuation: [{ urlBuilder: valuationUrl, target: PdfExportTargets.EXTERNAL_VALUATION }],
      approachFutureExit: [{ urlBuilder: valuationUrl, target: PdfExportTargets.FUTURE_EXIT }],
      approachPublicComps: [{ urlBuilder: valuationUrl, target: PdfExportTargets.PUBLIC_COMPS }],
      approachTransactionComps: [{ urlBuilder: valuationUrl, target: PdfExportTargets.TRANSACTION_COMPS }],
      approachSpecifiedShareValues: [{ urlBuilder: valuationUrl, target: PdfExportTargets.SPECIFIED_SHARE_VALUES }],
      approachCalibration: [{ urlBuilder: valuationUrl, target: PdfExportTargets.CALIBRATION }],
    };

    switch (configuration.selectedOption) {
      case PdfExportOption.VALUATIONS:
        if (companySlug) {
          const { companyOptions, companyOptionsOrder } = configuration;
          companyOptionsOrder.forEach(key => {
            if (companyOptions[key] && companyOptionMapping[key]) {
              companyOptionMapping[key].forEach(({ urlBuilder, target }) => {
                pushPage(urlBuilder, target.title, target.id, 'tab' in target ? target.tab : undefined);
              });
            }
          });
        }
        break;

      case PdfExportOption.FUND:
        if (fundSlug) {
          const { fundOptions } = configuration;

          if (fundOptions.summary) {
            pages.push({
              url: withQuery(fundSummaryUrl(firmSlug!, fundSlug), {
                ...commonQuery,
                exportTarget: PdfExportTargets.FUND_SUMMARY.id,
              }),
              title: PdfExportTargets.FUND_SUMMARY.title,
              exportTarget: PdfExportTargets.FUND_SUMMARY.id,
            });
          }
          if (fundOptions.scheduleOfInvestments) {
            pages.push({
              url: withQuery(fundScheduleOfInvestmentsUrl(firmSlug!, fundSlug), {
                ...commonQuery,
                exportTarget: PdfExportTargets.SCHEDULE_OF_INVESTMENTS.id,
              }),
              title: PdfExportTargets.SCHEDULE_OF_INVESTMENTS.title,
              exportTarget: PdfExportTargets.SCHEDULE_OF_INVESTMENTS.id,
            });
          }
        }
        break;

      case PdfExportOption.FIRM:
        if (firmSlug) {
          const { firmOptions } = configuration;

          if (firmOptions.summary) {
            const summarySlug = getRouteSlugById(FIRM_ROUTES, 'summary');
            if (summarySlug) {
              pages.push({
                url: withQuery(firmSummaryUrl(firmSlug!), {
                  ...commonQuery,
                  exportTarget: PdfExportTargets.FIRM_SUMMARY.id,
                }),
                title: PdfExportTargets.FIRM_SUMMARY.title,
                exportTarget: PdfExportTargets.FIRM_SUMMARY.id,
              });
            }
          }
        }
        break;

      default:
        // No additional URLs for unknown options
        break;
    }

    return pages;
  }, [configuration, location.search, params, companyInfo, firmInfo]);
};
